import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
    baseURL: API_BASE,
    withCredentials: true,
});

/**
 * Global 401 handler.
 *
 * When the auth cookies expire (or were never set), every protected admin
 * call would otherwise reject with an unhandled AxiosError — and because
 * many components use bare `.then(...)` without `.catch(...)`, those
 * rejections previously bubbled all the way up to React's "Uncaught
 * runtime errors" overlay, making it look like the panel crashed.
 *
 * We now intercept 401 once: notify any subscriber (the AuthContext) so it
 * can quietly transition the user to the anonymous state (which triggers
 * the standard `<Navigate to="/" />` gate in `<AdminPage>`), and swallow
 * the rejection so callers see a resolved-but-empty result instead of an
 * unhandled error.
 *
 * Routes that legitimately need a 401 (e.g. `/auth/me` during the initial
 * `AuthContext.refresh()` probe, or `/auth/login` with bad creds) are
 * opted out via the `_skip401Handler: true` request config flag.
 */
let onUnauthorized = null;
export function setUnauthorizedHandler(fn) {
    onUnauthorized = fn;
}

api.interceptors.response.use(
    (resp) => resp,
    (error) => {
        const status = error?.response?.status;
        const url = error?.config?.url || "";
        const skipByFlag = error?.config?._skip401Handler;
        // Auth endpoints must always pass their 401 through — a 401 there
        // means "bad credentials" / "not logged in", which is a normal
        // error for the calling component to handle. Defense in depth in
        // case an older bundle forgets to pass `_skip401Handler: true`.
        const isAuthEndpoint = /(^|\/)auth(\/|$)/.test(url);
        if (status === 401 && !skipByFlag && !isAuthEndpoint) {
            try { onUnauthorized?.(); } catch { /* noop */ }
            // Resolve with a synthetic empty response so component code that
            // does `.then(({ data }) => setX(data))` doesn't crash.
            return Promise.resolve({ data: null, status: 401, headers: {}, config: error.config });
        }
        return Promise.reject(error);
    },
);

export function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    }
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}
